"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useId, useState } from "react";
import Image from "next/image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWater } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
interface CurationWaveBadgeProps {
  readonly waveId: string;
  readonly tooltipId?: string | undefined;
  readonly onBadgeClick?: (() => void) | undefined;
  readonly size?: "default" | "compact" | undefined;
  readonly waveName?: string | null | undefined;
  readonly wavePfp?: string | null | undefined;
}
const getTrimmedText = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed;
};
export const CurationWaveBadge: React.FC<CurationWaveBadgeProps> = t0 => {
  const $ = _c(29);
  const {
    waveId,
    tooltipId: t1,
    onBadgeClick,
    size: t2,
    waveName,
    wavePfp
  } = t0;
  const tooltipId = t1 === undefined ? "wave-creator-badge" : t1;
  const size = t2 === undefined ? "default" : t2;
  const isMobile = useIsMobileDevice();
  const id = useId();
  const uniqueTooltipId = `${tooltipId}-${id}`;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [failedImageSrc, setFailedImageSrc] = useState(null);
  const showTooltip = isMobile === false;
  const dataTooltipId = showTooltip ? uniqueTooltipId : undefined;
  let t3;
  if ($[0] !== waveName) {
    t3 = getTrimmedText(waveName);
    $[0] = waveName;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const normalizedWaveName = t3;
  let t4;
  if ($[2] !== wavePfp) {
    const normalizedWavePfp = getTrimmedText(wavePfp);
    t4 = normalizedWavePfp ? getScaledImageUri(normalizedWavePfp, ImageScale.W_AUTO_H_50) : null;
    $[2] = wavePfp;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const waveImageSrc = t4;
  const tooltipContent = normalizedWaveName ?? "Featured wave";
  const ariaLabel = normalizedWaveName ? `Open ${normalizedWaveName}` : "Open featured wave";
  const buttonSizeClassName = size === "compact" ? "tw-h-[18px] tw-w-[18px]" : "tw-h-5 tw-w-5";
  const fallbackIconClassName = size === "compact" ? "tw-h-2 tw-w-2" : "tw-h-2.5 tw-w-2.5";
  const buttonClassName = `tw-inline-flex ${buttonSizeClassName} tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-white/15 tw-bg-white/5 tw-text-iron-200 tw-outline-none tw-ring-0 tw-transition-colors tw-duration-200 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/20 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-950 desktop-hover:hover:tw-border-white/25 desktop-hover:hover:tw-bg-white/10 desktop-hover:hover:tw-text-iron-100`;
  const showImage = waveImageSrc !== null && failedImageSrc !== waveImageSrc;
  const imageSrc = showImage ? waveImageSrc : null;
  const imageSize = size === "compact" ? 18 : 20;
  let t5;
  if ($[4] !== onBadgeClick) {
    t5 = e => {
      e.preventDefault();
      e.stopPropagation();
      setIsTooltipOpen(false);
      onBadgeClick?.();
    };
    $[4] = onBadgeClick;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsTooltipOpen(true);
    t7 = () => setIsTooltipOpen(false);
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[8] !== dataTooltipId) {
    t8 = dataTooltipId && {
      "data-tooltip-id": dataTooltipId
    };
    $[8] = dataTooltipId;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== fallbackIconClassName || $[11] !== imageSize || $[12] !== imageSrc) {
    t9 = imageSrc ? <Image src={imageSrc} alt="" width={imageSize} height={imageSize} unoptimized={true} className="tw-block tw-object-cover" style={{
      width: `${imageSize}px`,
      height: `${imageSize}px`,
      objectFit: "cover"
    }} onError={() => setFailedImageSrc(imageSrc)} /> : <FontAwesomeIcon icon={faWater} className={`${fallbackIconClassName} tw-flex-shrink-0 tw-text-current`} aria-hidden="true" />;
    $[10] = fallbackIconClassName;
    $[11] = imageSize;
    $[12] = imageSrc;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== ariaLabel || $[15] !== buttonClassName || $[16] !== t5 || $[17] !== t8 || $[18] !== t9 || $[19] !== waveId) {
    t10 = <button type="button" onClick={t5} onMouseEnter={t6} onMouseLeave={t7} className={buttonClassName} aria-label={ariaLabel} data-wave-id={waveId} {...t8}>{t9}</button>;
    $[14] = ariaLabel;
    $[15] = buttonClassName;
    $[16] = t5;
    $[17] = t8;
    $[18] = t9;
    $[19] = waveId;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isTooltipOpen || $[22] !== showTooltip || $[23] !== tooltipContent || $[24] !== uniqueTooltipId) {
    t11 = showTooltip && <Tooltip id={uniqueTooltipId} place="top" positionStrategy="fixed" offset={8} opacity={1} style={TOOLTIP_STYLES} isOpen={isTooltipOpen}><span className="tw-text-xs">{tooltipContent}</span></Tooltip>;
    $[21] = isTooltipOpen;
    $[22] = showTooltip;
    $[23] = tooltipContent;
    $[24] = uniqueTooltipId;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
};